import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import useProgramStripeIntegration from "app/utils/hooks/scheduling/useProgramStripeIntegration";
import styles from "../../../routes/ViewProgram/styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import GroupCard from "../GroupCard";
import GroupDetails from "../GroupDetails";
import Calendar from "app/images/calendar-dark.svg";
import Location from "app/images/location-dark.svg";
import Dollar from "app/images/dollar-dark.svg";
import CalendarX from "app/images/calendar-x-red.svg";
import Checkmark from "app/images/green-checkmark.svg";
import Waitlist from "app/images/waitlist.svg";
import MessageIcon from "app/images/blue-mail.svg";
import TimeIcon from "app/images/time.svg";
import EditIcon from "app/images/edit-icon.svg";
import Navbar from "../NavBar";
import RegisterStudentsPopup from "../RegisterStudentsPopup";
import Registrants from "../Registrants";
import TextAreaModal from "../TextAreaModal";
import {
  schedulingFetchDelete,
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import { RootState } from "typedefs";
import UserModel from "app/models/UserModel";
import Button from "app/components/Button/ButtonVariant";
import { ProgramModel } from "app/models/scheduling/ProgramModel";
import { useTranslation } from "react-i18next";
import SquaredColorOption from "../SquaredColorOption";
import {
  generateCurrencySymbolPriceAbbreviation,
  getColorCode,
} from "app/routes/BuildProgram/Pricing/utils";
import SimpleModal from "app/components/Programs/SimpleModal";
import InformationModal from "../InformationModal";
import { truncate } from "lodash";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { RoleType, UserContext } from "app/contexts/UserContext";
import { useUrlSearch } from "app/utils/hooks";
import { setAutoRegister } from "app/actions/scheduling/programRegistration";
import { fetchSelf } from "app/actions/user";
import { syncAthleteContact } from "app/utils/syncContact";
import AuthenticateModal from "app/routes/PublicCalendar/AuthenticateModal";
import PendingPurchaseBanner from "app/components/Programs/PendingPurchaseBanner";
import { Loading } from "app/components/Wrappers";
import RegisterAthleteModal from "../RegisterAthleteModal";
import { formatMonthDayYear, formatTime } from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

interface DeleteResponse {
  firstOccurence?: number;
}

const transformRegistrationResponse = (response, t) => {
  if (response?.registrationResult) {
    const { type } = response.registrationResult;
    if (type === "registered") {
      return {
        message: t("Registered"),
        type: "completed",
        details: t("Registration completed successfully"),
      };
    } else if (type === "waitlisted") {
      return {
        message: t("Waitlisted"),
        type: "waitlisted",
        details: t(
          "Your registration filled the remaining spots. Some of your registrants have been placed on the waitlist."
        ),
      };
    }
  }
  if (response?.message) {
    return response;
  }
  return {
    message: t("Registration complete"),
    type: "completed",
    details: "",
  };
};

const ViewProgramCard = ({
  program: initialProgram,
  programId,
  setRefetch,
  isPublic = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { preferences } = useCalendarPreferences();
  const autoRegister = useSelector(
    (state: RootState): boolean => state.programRegistration.autoRegister,
  );
  const [program, setProgram] = useState(initialProgram);
  const [programModel, setProgramModel] = useState<ProgramModel | null>(null);
  const [isProgramLoading, setIsProgramLoading] = useState(!initialProgram);
  const [showRegistrans, setShowRegistrans] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [messageModal, setMessageModal] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [showPublicRegisterPopup, setShowPublicRegisterPopup] = useState(false);
  const [showCancelEventPopup, setShowCancelEventPopup] = useState(false);
  const [eventItem, setEventItem] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelWithoutRefund, setCancelWithoutRefund] = useState(false);
  const userState = useSelector(
    (state: RootState): UserModel => state.user.data,
  );
  const user = useContext(UserContext);
  const userType = user.role;
  const athleteView = userType === RoleType.ATHLETE;
  const userId = userState?.id;
  const [message, setMessage] = useState("");
  const [messageProgramId, setMessageProgramId] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [deleteModalTexts, setDeleteModalTexts] = useState<{
    title: string;
    subTitle: string;
    subTitle2: string | React.ReactNode;
    placeholder: string;
  }>({
    title: t('Are you sure you want to cancel this event?'),
    subTitle: t('Send a message via email to the registered and waitlisted students.'),
    subTitle2: t('Pro-rate refunds will NOT be issued. Manage refunds directly within your Stripe account.'),
    placeholder: t("Ex: Today's clinic is canceled due to inclement weather."),
  });

  // Compute participant status from programModel
  const participantStatus = programModel
    ? (() => {
        const isCompleted = programModel.fullyCompletedRegistrants.some(
          (student) => student.studentProfile.id === userId?.toString()
        );
        const isWaitlisted = programModel.waitlistedRegistrants.some(
          (student) => student.studentProfile.id === userId?.toString()
        );
        return isCompleted ? "registered" : isWaitlisted ? "waitlisted" : "";
      })()
    : "";

  // CN-1323 (CFTB-3293): registered managed dependents of the current user
  // (a dependent's studentProfile.headExternalId is the head's external id), so
  // a parent sees a family member is registered even when they themselves aren't.
  const registeredFamilyNames =
    athleteView && userId && programModel
      ? programModel.fullyCompletedRegistrants
          .filter((s) => s.studentProfile.headExternalId === userId.toString())
          .map((s) => s.studentProfile.firstName)
      : [];

  const noRegisteredParticipants = programModel?.completedRegistrants.length || 0;

  const hasStripeIntegration = useProgramStripeIntegration(programModel);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);
  const [showGeneral, setShowGeneral] = useState(true);
  const isClinic = program?.attendance === 'clinic';
  // A drop-in's events include past dates, so lead with the next one still
  // to come; fall back to the last date once they have all passed (CN-2867).
  const headerEvent = isClinic
    ? program?.events?.find((e) => DateTime.fromISO(e.endTime) > DateTime.now())
      ?? program?.events?.[program.events.length - 1]
    : program?.events?.[0];
  const options = [
    { title: t("General"), isActive: activeTab === "general", code: "general" },
    { title: t("Details"), isActive: activeTab === "details", code: "details" },
  ];

  if (!isClinic && (!isPublic || userState)) {
    options.push({
      title: t("Registrants"),
      isActive: activeTab === "registration",
      code: "registration",
    });
  }

  useEffect(() => {
    if (initialProgram) {
      setProgram(initialProgram);
      setProgramModel(new ProgramModel(initialProgram));
      setIsProgramLoading(false);
    }
  }, [initialProgram]);

  const handlePendingRegistrationCancelled = async () => {
    try {
      const updatedProgram = await schedulingFetchGet(`/programs/${programId}`, {}, {}, null, isPublic);
      setProgram(updatedProgram);
      setProgramModel(new ProgramModel(updatedProgram));
    } catch (error) {
      console.error('Error fetching updated program data:', error);
      toast.error(t("Could not refresh program data. Please reload the page."));
    }
  };

  const updateFromRegistrationResponse = (response) => {
    if (!response || !response.id) {
      return;
    }

    setProgram(response);
    setProgramModel(new ProgramModel(response));
  };

  const handleRegistrationSuccess = async (response) => {
    if (!response || !response.id) {
      try {
        const updatedProgram = await schedulingFetchGet(`/programs/${programId}`, {}, {}, null, isPublic);
        setProgram(updatedProgram);
        setProgramModel(new ProgramModel(updatedProgram));
      } catch (error) {
        console.error('Error fetching updated program data:', error);
        toast.warning(t("Registration succeeded, but the page could not refresh. Please reload to see updated data."));
      }
      return;
    }

    updateFromRegistrationResponse(response);
    const modalMessage = transformRegistrationResponse(response, t);
    setModalInfo(modalMessage);
    setShowInformationModal(true);
  };


  const autoRegisteredRef = useRef(false);
  const [searchParams] = useUrlSearch();

  // Auto-register the current athlete after they sign in from a public program
  // page. Waits for the program to load so the coach external id is available,
  // then syncs the athlete into the scheduling service before registering — a
  // sync failure blocks the registration. The coach id prefers the coach_id
  // URL param (server links rely on it to attribute the contact to a specific
  // coach) and falls back to the program data when the param is absent.
  useEffect(() => {
    if (!autoRegister || userType !== RoleType.ATHLETE) {
      return;
    }
    if (!programModel || !userState || autoRegisteredRef.current) {
      return;
    }
    autoRegisteredRef.current = true;

    const registerCurrentAthlete = async () => {
      try {
        await syncAthleteContact(
          userState,
          searchParams.coach_id || programModel.coaches[0]?.externalId,
        );
        await (dispatch as any)(fetchSelf());

        const response = await schedulingFetchPost(
          `/programs/${programId}/register`,
          { student_profile_ids: [{ id: userState.id }] },
        );
        handleRegistrationSuccess(response);
      } catch (error) {
        console.error("Auto-registration failed:", error);
        toast.error(t("Registration could not be completed automatically. Please try registering again."));
      } finally {
        dispatch(setAutoRegister(false));
      }
    };

    registerCurrentAthlete();
  }, [autoRegister, userType, programModel, userState]);

  const formattedDate = (dateString) => {
    const date = DateTime.fromISO(dateString, {
      zone: program?.timeZone,
    });

    return formatMonthDayYear(date, preferences.dateFormat);
  };

  const cardSubtitle = useMemo(() => {
    const subtitle = program?.recurrenceGroup?.subtitle || program?.subtitle;
    if (!program?.start) {
      return subtitle || "";
    }
    const dt = DateTime.fromISO(program.start, { zone: program?.timeZone });
    return `${dt.toLocaleString({ weekday: "long" })}, ${formatTime(dt, preferences.timeFormat)}`;
  }, [program?.start, program?.timeZone, program?.subtitle, program?.recurrenceGroup?.subtitle, preferences.timeFormat]);

  const handleDelete = (item: number, type?: string, title?: string, date?: DateTime) => {
    if (type === 'clinic') {
      const formattedDate = date ? date.toFormat('EEEE MMMM d, yyyy') : '';

      const subTitle2 = (
        <>
          {t('Are you sure you want to delete')} <strong>{title} on {t(formattedDate)}?</strong>
        </>
      );

      setDeleteModalTexts(prev => ({
        ...prev,
        title: t('Delete Clinic?'),
        subTitle: t('All the registered, waitlisted athletes and coaches will be notified via email.'),
        subTitle2,
      }));
    }

    setShowCancelEventPopup(true);
    setEventItem(item);
  };

  const handleRemoveEvent = () => {
    schedulingFetchDelete(`/programs/${eventItem}`, {
      reason: cancelReason,
      ...(cancelWithoutRefund ? { skip_refund: true } : {}),
    }).then((response: DeleteResponse) => {
      setShowCancelEventPopup(false);
      setEventItem(null);
      setCancelReason("");
      setCancelWithoutRefund(false);
      window.location.replace(`/programs/${response?.firstOccurence}/view`);
    }).catch((error) => {
      console.error("Failed to cancel event:", error);
      toast.error(t("Failed to cancel the event. Please try again."));
    });
  };

  const getImage = (type) => {
    switch (type) {
      case "waitlisted":
        return TimeIcon;
      case "completed":
        return Checkmark;
      case "full":
        return CalendarX;
      case "partial":
        return EditIcon;
      default:
        return Checkmark;
    }
  };

  const handleRegistrantDeleted = (response) => {
    if (response) {
      setProgram(response);
      setProgramModel(new ProgramModel(response));
    }
    setModalInfo({ message: t("Registration canceled successfully") });
    setShowInformationModal(true);
  };


  const handleSendMessage = async () => {
    const targetId = messageProgramId || programId;
    const params = {
      message,
    };

    if (message === "") {
      setMessageModal(false);
      setMessageProgramId(null);
      return;
    }

    try {
      await schedulingFetchPost(`/programs/${targetId}/send_message`, params);

      setMessageModal(false);
      setMessage("");
      setMessageProgramId(null);
      toast.success(t("Message sent successfully."));
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error(t("Failed to send message. Please try again."));
    }
  };

  const handleOccurrenceMessage = (occurrenceProgramId) => {
    setMessageProgramId(occurrenceProgramId);
    setMessageModal(true);
  };

  const handlePublicConfirmation = () => {
    dispatch(setAutoRegister(true));
    const returnUrl = `/public/programs/${program.id}/view?isSignIn=true${isPublic ? `&coach_id=${program.coaches[0]?.externalId}&phoneRequired=true` : ''}`;
    (window as any).location = `/signin?return_url=${encodeURIComponent(returnUrl)}`;
  };

  const handleTabClick = (code: string) => {
    if (code === "registration") {
      setShowRegistrans(true);
      setShowGeneral(false);
      setShowDetails(false);
      setActiveTab("registration");
    } else if (code === "details") {
      setShowDetails(true);
      setShowGeneral(false);
      setShowRegistrans(false);
      setActiveTab("details");
    } else {
      setActiveTab("general");
      setShowRegistrans(false);
      setShowDetails(false);
      setShowGeneral(true);
    }
  };

  const groupCards = () => {
    return program?.events?.map((dateString, index) => {
      const startTimestr = dateString.localStartTime;
      const date = DateTime.fromISO(startTimestr, {
        zone: program?.timeZone,
      });
      const endDate = DateTime.fromISO(dateString.localEndTime, {
        zone: program?.timeZone,
      });
      const id = dateString.eventableId;

      const event = {
        id: dateString.eventableId,
        startDate: date,
        endDate,
        registrationStatus: dateString.registrationStatus,
        registrationStatusCode: dateString.registrationStatusCode,
        title: dateString.title,
        collectType: dateString.collectType,
        spotsBooked: dateString.spotsBooked,
        capacity: dateString.capacity,
        externalThumbnailUrl: dateString.externalThumbnailUrl,
      };

      return (
        <GroupCard
          key={index}
          eventId={id}
          program={program}
          multipleDate={date}
          handleDelete={handleDelete}
          handleMessage={handleOccurrenceMessage}
          event={event}
          setRefetch={setRefetch}
          isPublic={isPublic}
          setShowPublicRegisterPopup={setShowPublicRegisterPopup}
        />
      );
    });
  };

  const renderActionButton = () => {
    // Public user (unauthenticated)
    const isFullWithoutWaitlist =
        program?.spotsBooked >= program?.capacity &&
        !programModel?.withWaitlist;
    if (!user.role) {
      return (
        <div className={styles.buttonsContainer}>
          <Button
            buttonType="primary"
            title={t("Register")}
            onClickFunc={() => setShowPublicRegisterPopup(true)}
          />
        </div>
      );
    }

    // Student/Athlete
    if (user.role === RoleType.ATHLETE || user.role === RoleType.OTHER) {
      const status = program?.registrationStatusCode;
      const isDisabled = status === "closed" ||
                         status === "not_started" ||
                         program?.hasPendingRegistrations === true ||
                         isFullWithoutWaitlist ||
                         (!programModel.allowAdditionalPeople && participantStatus === "registered");
      return (
        <div className={styles.buttonsContainer}>
          <Button
            buttonType={isDisabled ? "secondary" : "primary"}
            title={t("Register")}
            onClickFunc={isDisabled ? undefined : () => setShowRegisterPopup(true)}
            disabled={isDisabled}
          />
        </div>
      );
    }

    // Coach/Admin/Scheduler
    const showRegisterButton = program?.status !== "completed";
    const showSendMessage = noRegisteredParticipants > 0;

    if ((showRegisterButton || showSendMessage) && !isClinic) {
      return (
        <div className={styles.buttonsContainer}>
          {showRegisterButton && (
            <Button
              buttonType="primary"
              title={t("Register Athletes")}
              onClickFunc={() => setShowRegisterModal(true)}
            />
          )}
          {showSendMessage && (
            <Button
              buttonType="light-primary"
              title={t("Send Message")}
              onClickFunc={() => setMessageModal(true)}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const rightPanel = () => {
    return (
      <div className={styles.right_width}>
        {renderActionButton()}
        {athleteView && program?.status !== "completed" && (
          <>
            {(participantStatus === "registered" && (
              <div className={styles.status_container}>
                <img src={Checkmark} />
                <p className={styles.message}>{t("You are registered")}</p>
              </div>
            )) ||
              (participantStatus === "waitlisted" && (
                <div className={styles.status_container}>
                  <img src={Waitlist} />
                  <p className={styles.message}>{t("You are waitlisted")}</p>
                </div>
              )) ||
              (registeredFamilyNames.length > 0 && (
                <div className={styles.status_container}>
                  <img src={Checkmark} />
                  <p className={styles.message}>
                    {registeredFamilyNames.length === 1
                      ? t("{{name}} is registered", {
                          name: registeredFamilyNames[0],
                        })
                      : t("{{count}} family members registered", {
                          count: registeredFamilyNames.length,
                        })}
                  </p>
                </div>
              ))}
          </>
        )}
      </div>
    );
  };

  const coachId = program?.coaches[0].externalId;

  if (isProgramLoading || !program || !programModel) {
    return (
      <div className={styles.card}>
        <div className={styles.loadingContainer}>
          <Loading loadType="spinner" isLoading />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.card}>
        <PendingPurchaseBanner
          programId={programId}
          onCancelled={handlePendingRegistrationCancelled}
        />
        <div className={styles.line_space_between}>
          <div className={styles.gap16}>
            <p className={styles.cardTitle}>
              {truncate(program?.recurrenceGroup?.title || program?.title, { length: 75 })}
            </p>
            <p className={styles.cardSubtitle}>{cardSubtitle}</p>
          </div>
          {!isClinic && rightPanel()}
        </div>
        {!isClinic && (
          <div className={styles.line}>
            {program?.registrationStatus && (
              <div className={styles.registration_status}>
                <SquaredColorOption
                  customContainer={styles.custom_registration}
                  color={getColorCode(program?.registrationStatusCode)}
                  text={program?.registrationStatus}
                />
              </div>
            )}
            <div className={styles.spotsBooked}>
              {program?.spotsBooked}/{program?.capacity} {t("Spots Booked")}
            </div>
          </div>
        )}

        <div className={styles.navbar}>
          <Navbar handleClick={handleTabClick} options={options} />
        </div>
        {showGeneral && (
          <>
            <div className={styles.cardInfo}>
              <div className={styles.cardInfoMain}>
                <span className={styles.icons}>
                  <img src={Calendar} />
                </span>
                <span className={styles.distanceTextWithIcon}>
                  {formattedDate(headerEvent?.startTime)}
                </span>
              </div>
              <div className={styles.cardInfoMain}>
                <span className={styles.icons}>
                  <img src={Location} />
                </span>
                <span className={styles.distanceTextWithIcon}>
                  {program?.location}
                </span>
              </div>
              <div className={styles.cardInfoMain}>
                <span className={styles.icons}>
                  <img src={Dollar} />
                </span>
                <span className={styles.distanceTextWithIcon}>
                  {program?.free
                    ? t('Free')
                    : generateCurrencySymbolPriceAbbreviation(program?.recurrenceGroup?.priceCurrency || program?.priceCurrency, program?.recurrenceGroup?.price || program?.price)}
                </span>
              </div>
            </div>
            <div className={styles.description}>
              {t(program?.recurrenceGroup?.shortDescription) || t(program?.shortDescription)}
            </div>
            <div id="group-card" className={styles.group_cards_container}>{groupCards()}</div>
          </>
        )}
        {showRegistrans && programModel && (
          <div>
            <Registrants
              programModel={programModel}
              userType={userType}
              onRegistrationSuccess={handleRegistrationSuccess}
              onRegistrantDeleted={handleRegistrantDeleted}
              onTransferComplete={() => setRefetch?.(true)}
            />
          </div>
        )}

        {showDetails && (
          <div>
            <GroupDetails program={program} />
          </div>
        )}

        <SimpleModal
          isOpen={showPublicRegisterPopup}
          title={t("Authentication required")}
          buttonText={t("Sign In / Sign Up")}
          onCancel={() => setShowPublicRegisterPopup(false)}
          onConfirm={handlePublicConfirmation}
        >
          <div className={styles.login_modal}>
            <div>
              {t("Please sign in or sign up to complete your program.")}
            </div>
          </div>
        </SimpleModal>
        <AuthenticateModal
          isOpen={showPublicRegisterPopup}
          onClose={() => setShowPublicRegisterPopup(false)}
          flow="programs"
          urlValues={{ programId: program?.id, coachId }}
          headerValues={{ clinic: program?.title }}
        />

        {showInformationModal && (
          <InformationModal
            topImage={getImage(modalInfo?.type)}
            title={modalInfo?.message}
            subtitle={modalInfo?.details}
            handleAction={() => {
              setShowInformationModal(false);
            }}
          />
        )}
        {showRegisterPopup && programModel && (
          <RegisterStudentsPopup
            programModel={programModel}
            showModal={setShowRegisterPopup}
          />
        )}

        {messageModal && (
          <TextAreaModal
            topImage={MessageIcon}
            showCloseIcon
            title={t("Message")}
            subTitle={
              messageProgramId
                ? t("Send a message via email to the athletes registered for this occurrence.")
                : t("Send a message via email to the registered athletes.")
            }
            placeholder={t("Type here")}
            width="400px"
            customConfirmStyle={
              message !== "" ? styles.message_button : styles.disabled
            }
            confirmDisabled={message === ""}
            value={message}
            setValue={setMessage}
            stackedButtons
            confirmFirst
            confirmText={t("Send")}
            cancelText={t("Cancel")}
            setShowModal={(show) => {
              setMessageModal(show);
              if (!show)  {
                setMessageProgramId(null);
              }
            }}
            handleAction={handleSendMessage}
          />
        )}
        {showRegisterModal && programModel && (
          <RegisterAthleteModal
            programModel={programModel}
            isModalOpen={showRegisterModal}
            modalHandler={() => setShowRegisterModal(false)}
            stripeIntegrationEnabled={hasStripeIntegration}
            onRegistrationSuccess={handleRegistrationSuccess}
          />
        )}
        {showCancelEventPopup && (
          <TextAreaModal
            topImage={CalendarX}
            title={deleteModalTexts.title}
            subTitle={deleteModalTexts.subTitle}
            subTitle2={deleteModalTexts.subTitle2}
            placeholder={deleteModalTexts.placeholder}
            width="400px"
            stackedButtons
            showCloseIcon
            confirmFirst
            setValue={setCancelReason}
            value={cancelReason}
            additionalContent={
              isClinic &&
              (programModel?.collectType === "online" ||
                programModel?.collectType === "both") && (
                <>
                  {!cancelWithoutRefund && (
                    <p className={styles.refund_notice}>
                      {t("Online paying participants will be refunded for this session through Stripe.")}
                    </p>
                  )}
                  <label className={styles.cancel_without_refund}>
                    <input
                      type="checkbox"
                      checked={cancelWithoutRefund}
                      onChange={(event) => setCancelWithoutRefund(event.target.checked)}
                    />
                    {t("Cancel without issuing Stripe refunds")}
                  </label>
                </>
              )
            }
            confirmText={t("Yes, cancel it")}
            cancelText={t("No, keep it")}
            setShowModal={(value) => {
              setShowCancelEventPopup(value);
              if (!value) { setCancelWithoutRefund(false); }
            }}
            handleAction={handleRemoveEvent}
          />
        )}
      </div>
    </>
  );
};

export default ViewProgramCard;
